import React, { useEffect, useState, useMemo } from "react";
import FarmerLayout from "../../components/layouts/FarmerLayout";
import FarmerRealTimeBalanceCard from "./components/FarmerRealTimeBalanceCard";
import FarmerAdvanceBalanceCard from "./components/FarmerAdvanceBalanceCard";
import { auth, database, firestore } from "../../firebaseConfig";
import { ref, onValue, off } from "firebase/database";
import { doc, getDoc } from "firebase/firestore";
import toast from "react-hot-toast";
import MessagingPanel from "../../components/messaging/MessagingPanel";
import { PAGE_SIZE, paginateItems, rangeForPage } from "./pagination";

const MONTHS = [
  "January","February","March","April","May","June",
  "July","August","September","October","November","December"
];

const FarmerPayments = () => {
  const [companyName, setCompanyName] = useState("");
  const [otherName, setOtherName] = useState("");
  const [balance, setBalance] = useState(0);
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [advanceBalance, setAdvanceBalance] = useState(0);
  const [advanceLoading, setAdvanceLoading] = useState(true);

  const today = new Date();
  const [selectedYear, setSelectedYear] = useState(today.getFullYear());
  const [selectedMonthIndex, setSelectedMonthIndex] = useState(today.getMonth());

  const [payments, setPayments] = useState([]); // all payments for selected month
  const [loading, setLoading] = useState(true);
  const [advanceTransactions, setAdvanceTransactions] = useState([]); // advance transactions for selected month
  const [advanceTxLoading, setAdvanceTxLoading] = useState(true);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [advancePage, setAdvancePage] = useState(1);

  // otherName maps to the farmer's identifier used in messaging threads
  const messagingParticipantId = otherName;

  // ----------------------------------------------------
  // STEP 1 — Load farmer profile: companyName + OtherName + Balance
  // ----------------------------------------------------
  useEffect(() => {
    const unsub = auth.onAuthStateChanged(async (user) => {
      if (!user) return;

      try {
        // fetch farmer profile in Firestore → "users"
        const userRef = doc(firestore, "users", user.uid);
        const userSnap = await getDoc(userRef);

        if (!userSnap.exists()) {
          toast.error("Profile not found");
          setBalanceLoading(false);
          return;
        }

        const data = userSnap.data();
        setCompanyName(data.companyName);
        setOtherName(data.otherName);

        // fetch balance from: Companies/{company}/ValidatedFarmers/{OtherName}/Balance
        const balRef = ref(
          database,
          `Companies/${data.companyName}/ValidatedFarmers/${data.otherName}/Balance`
        );
        onValue(balRef, (snap) => {
          setBalance(snap.val() || 0);
          setBalanceLoading(false);
        });

        // fetch advance balance from: Companies/{company}/ValidatedFarmers/{OtherName}/AdvanceBalance
        const advBalRef = ref(
          database,
          `Companies/${data.companyName}/ValidatedFarmers/${data.otherName}/AdvanceBalance`
        );
        onValue(advBalRef, (snap) => {
          setAdvanceBalance(snap.val() || 0);
          setAdvanceLoading(false);
        });
      } catch (err) {
        console.error(err);
        toast.error("Failed to load profile");
        setBalanceLoading(false);
      }
    });

    return () => unsub();
  }, []);

  // ----------------------------------------------------
  // STEP 2 — Load payments for selected month
  // path: Companies/company/FarmerRecords/OtherName/Payments/year/month
  // ----------------------------------------------------
  useEffect(() => {
    if (!companyName || !otherName) return;

    setLoading(true);
    const year = selectedYear;
    const monthName = MONTHS[selectedMonthIndex];

    const path = `Companies/${companyName}/FarmerRecords/${otherName}/Payments/${year}/${monthName}`;

    const r = ref(database, path);

    // eslint-disable-next-line no-unused-vars
    const unsub = onValue(
      r,
      (snap) => {
        const raw = snap.val() || {};
        const arr = Object.values(raw).sort(
          (a, b) => new Date(b.TransactionDate) - new Date(a.TransactionDate)
        );
        setPayments(arr);
        setLoading(false);
      },
      (err) => {
        console.error(err);
        toast.error("Failed to load payments");
        setLoading(false);
      }
    );

    return () => off(r);
  }, [companyName, otherName, selectedYear, selectedMonthIndex]);

  // ----------------------------------------------------
  // STEP 2.5 — Load advance transactions for selected month
  // path: Companies/company/FarmerRecords/OtherName/AdvanceTransactions/year/month
  // ----------------------------------------------------
  useEffect(() => {
    if (!companyName || !otherName) return;

    setAdvanceTxLoading(true);
    const year = selectedYear;
    const monthName = MONTHS[selectedMonthIndex];

    const path = `Companies/${companyName}/FarmerRecords/${otherName}/AdvanceTransactions/${year}/${monthName}`;

    const r = ref(database, path);

    const unsub = onValue(
      r,
      (snap) => {
        const raw = snap.val() || {};
        const arr = Object.values(raw).sort(
          (a, b) => new Date(b.TransactionDate) - new Date(a.TransactionDate)
        );
        setAdvanceTransactions(arr);
        setAdvanceTxLoading(false);
      },
      (err) => {
        console.error(err);
        toast.error("Failed to load advance transactions");
        setAdvanceTxLoading(false);
      }
    );

    return () => off(r);
  }, [companyName, otherName, selectedYear, selectedMonthIndex]);

  useEffect(() => {
    setPaymentsPage(1);
    setAdvancePage(1);
  }, [selectedYear, selectedMonthIndex, companyName, otherName]);

  // ----------------------------------------------------
  // STEP 3 — Compute summaries
  // ----------------------------------------------------
  const totalPaid = useMemo(() => {
    return payments
      .filter((p) => p.Status !== "Pending")
      .reduce((sum, p) => sum + parseFloat(p.Amount || 0), 0);
  }, [payments]);

  const totalAdvance = useMemo(() => {
    return payments
      .filter((p) => p.Status !== "Pending" && p.Details?.toLowerCase().includes("advance"))
      .reduce((sum, p) => sum + parseFloat(p.Amount || 0), 0);
  }, [payments]);

  const paymentsTotalPages = Math.max(1, Math.ceil(payments.length / PAGE_SIZE));
  const advanceTotalPages = Math.max(
    1,
    Math.ceil(advanceTransactions.length / PAGE_SIZE)
  );

  useEffect(() => {
    if (paymentsPage > paymentsTotalPages) {
      setPaymentsPage(paymentsTotalPages);
    }
  }, [paymentsTotalPages, paymentsPage]);

  useEffect(() => {
    if (advancePage > advanceTotalPages) {
      setAdvancePage(advanceTotalPages);
    }
  }, [advanceTotalPages, advancePage]);

  const paginatedPayments = useMemo(
    () => paginateItems(payments, paymentsPage),
    [payments, paymentsPage]
  );

  const paginatedAdvanceTransactions = useMemo(
    () => paginateItems(advanceTransactions, advancePage),
    [advanceTransactions, advancePage]
  );

  const paymentsRange = useMemo(
    () => rangeForPage(payments, paymentsPage),
    [payments, paymentsPage]
  );
  const advanceRange = useMemo(
    () => rangeForPage(advanceTransactions, advancePage),
    [advanceTransactions, advancePage]
  );

  return (
    <FarmerLayout>
      <div className="p-6 max-w-5xl mx-auto">
        <h1 className="text-2xl font-bold text-green-700">Payments</h1>
        <p className="text-gray-600 mb-4">
          Your milk payments, advances, and account balance.
        </p>

        {/* ---------------- REAL-TIME BALANCE CARDS ---------------- */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <FarmerRealTimeBalanceCard balance={balance} loading={balanceLoading} />
          <FarmerAdvanceBalanceCard advanceBalance={advanceBalance} loading={advanceLoading} />
        </div>

        {/* ---------------- MONTHLY SUMMARY CARDS ---------------- */}
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-gray-700 mb-3">Monthly Breakdown</h2>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
          <div className="p-4 bg-white rounded shadow border-l-4 border-cyan-600">
            <h3 className="text-gray-600">Total Paid (This Month)</h3>
            <p className="text-xl font-bold text-cyan-700">
              KES {totalPaid.toLocaleString()}
            </p>
          </div>

          <div className="p-4 bg-white rounded shadow border-l-4 border-orange-500">
            <h3 className="text-gray-600">Advances (This Month)</h3>
            <p className="text-xl font-bold text-orange-600">
              KES {totalAdvance.toLocaleString()}
            </p>
          </div>
        </div>

        {/* ---------------- FILTERS ---------------- */}
        <div className="flex items-center gap-3 mb-6">
          <select
            className="border px-3 py-2 rounded"
            value={selectedMonthIndex}
            onChange={(e) => setSelectedMonthIndex(Number(e.target.value))}
          >
            {MONTHS.map((m, i) => (
              <option key={m} value={i}>{m}</option>
            ))}
          </select>

          <select
            className="border px-3 py-2 rounded"
            value={selectedYear}
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {[selectedYear - 1, selectedYear, selectedYear + 1].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>

        {/* ---------------- PAYMENTS LIST ---------------- */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          <div className="bg-white p-4 rounded shadow h-[32rem] flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold">Payment Records</h3>
              <span className="text-sm text-gray-500">
                Page {paymentsPage} of {paymentsTotalPages}
              </span>
            </div>

            <div className="flex-1">
              {loading ? (
                <div className="h-full flex items-center justify-center text-gray-500">
                  Loading...
                </div>
              ) : payments.length === 0 ? (
                <div className="h-full flex items-center justify-center text-gray-500 text-center">
                  No payments this month.
                </div>
              ) : (
                <div className="space-y-3 overflow-y-auto h-full pr-1">
                  {paginatedPayments.map((p) => (
                    <div
                      key={p.Id}
                      className="p-3 border rounded flex justify-between items-center"
                    >
                      <div>
                        <p className="font-semibold text-gray-800">
                          KES {Number(p.Amount).toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-500">{p.Details}</p>
                        <p className="text-xs text-gray-400">
                          {new Date(p.TransactionDate).toLocaleString()}
                        </p>
                      </div>

                      <span
                        className={`px-3 py-1 rounded text-sm ${
                          p.Status === "Pending"
                            ? "bg-yellow-100 text-yellow-700"
                            : "bg-green-100 text-green-700"
                        }`}
                      >
                        {p.Status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-3 mt-3 border-t flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {paymentsRange.start}-{paymentsRange.end} of {payments.length} records
              </p>
              <div className="flex gap-2">
                <button
                  className="px-3 py-1 border rounded disabled:opacity-50"
                  onClick={() => setPaymentsPage((p) => Math.max(1, p - 1))}
                  disabled={paymentsPage === 1 || loading}
                  type="button"
                >
                  Previous
                </button>
                <button
                  className="px-3 py-1 border rounded disabled:opacity-50"
                  onClick={() =>
                    setPaymentsPage((p) =>
                      Math.min(paymentsTotalPages, p + 1)
                    )
                  }
                  disabled={paymentsPage >= paymentsTotalPages || loading}
                  type="button"
                >
                  Next
                </button>
              </div>
            </div>
          </div>

          {/* ---------------- ADVANCE TRANSACTIONS LIST ---------------- */}
          <div className="bg-white p-4 rounded shadow h-[32rem] flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-lg font-semibold text-purple-700">Advance Transaction History</h3>
                <p className="text-sm text-gray-600">
                  View when advances were credited and how they've been paid off
                </p>
              </div>
              <span className="text-sm text-gray-500">
                Page {advancePage} of {advanceTotalPages}
              </span>
            </div>

            <div className="flex-1">
              {advanceTxLoading ? (
                <div className="h-full flex items-center justify-center text-gray-500">
                  Loading...
                </div>
              ) : advanceTransactions.length === 0 ? (
                <div className="h-full flex items-center justify-center text-gray-500 text-center">
                  No advance transactions this month.
                </div>
              ) : (
                <div className="space-y-3 overflow-y-auto h-full pr-1">
                  {paginatedAdvanceTransactions.map((tx) => (
                    <div
                      key={tx.Id}
                      className="p-3 border rounded flex justify-between items-center"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-800">
                            {tx.TransactionType === "add" ? "+" : "-"} KES {Number(tx.Amount).toLocaleString()}
                          </p>
                          <span
                            className={`px-2 py-1 rounded text-xs ${
                              tx.TransactionType === "add"
                                ? "bg-purple-100 text-purple-700"
                                : "bg-orange-100 text-orange-700"
                            }`}
                          >
                            {tx.TransactionType === "add" ? "Advance Given" : "Advance Deducted"}
                          </span>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">{tx.Details}</p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(tx.TransactionDate).toLocaleString()}
                        </p>
                      </div>

                      <div className="text-right text-sm">
                        <p className="text-gray-600">Previous: KES {Number(tx.PreviousBalance || 0).toLocaleString()}</p>
                        <p className="font-semibold text-purple-700">New: KES {Number(tx.NewBalance || 0).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="pt-3 mt-3 border-t flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {advanceRange.start}-{advanceRange.end} of {advanceTransactions.length} records
              </p>
              <div className="flex gap-2">
                <button
                  className="px-3 py-1 border rounded disabled:opacity-50"
                  onClick={() => setAdvancePage((p) => Math.max(1, p - 1))}
                  disabled={advancePage === 1 || advanceTxLoading}
                  type="button"
                >
                  Previous
                </button>
                <button
                  className="px-3 py-1 border rounded disabled:opacity-50"
                  onClick={() =>
                    setAdvancePage((p) =>
                      Math.min(advanceTotalPages, p + 1)
                    )
                  }
                  disabled={advancePage >= advanceTotalPages || advanceTxLoading}
                  type="button"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ---------------- MESSAGING ---------------- */}
        <div className="mt-8 space-y-3">
          <h3 className="text-xl font-semibold text-gray-800">Message your company</h3>
          <p className="text-sm text-gray-600">
            Reach out like WhatsApp, see when messages are read, and remove messages for everyone within 5 minutes or before they are read.
            All conversations are AI monitored for content violations.
          </p>
          <MessagingPanel
            companyName={companyName}
            participantId={messagingParticipantId}
            actorType="participant"
            title="Farmer ↔ Company chat"
            containerClassName="max-w-4xl mx-auto"
          />
        </div>
      </div>
    </FarmerLayout>
  );
};

export default FarmerPayments;
